// @ts-nocheck
import React, {useEffect, useMemo, useState} from 'react'
import {AgGridReact} from '@ag-grid-community/react'

import api from '../../controlRequests/api/api'
import {getGraphDayState, getReasonAbbreviation, getReasonStripeBackground} from '../../controlRequests/utils/graphDay'


const GraphWellHistoryModal = ({row, onClose}) => {
    const [months, setMonths] = useState('all')
    const [rows, setRows] = useState([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')

    useEffect(() => {
        if (!row?.wells?.id) return

        setLoading(true)
        setError('')
        api.get('well-history/', {
            params: {
                well: row.wells.id,
                months,
            },
        })
            .then((response) => setRows(response.data?.results || []))
            .catch((requestError) => {
                console.error(requestError)
                setRows([])
                setError(requestError.response?.data?.detail || 'Не удалось загрузить историю графика')
            })
            .finally(() => setLoading(false))
    }, [row?.wells?.id, months])

    const columnDefs = useMemo(() => {
        const dayColumns = Array.from({length: 31}, (_, index) => {
            const day = index + 1
            return {
                field: `day_${day}`,
                headerName: String(day),
                width: 36,
                minWidth: 32,
                maxWidth: 40,
                headerClass: 'graph-history-day-header',
                cellClass: 'graph-history-day-cell',
                sortable: false,
                filter: false,
                resizable: false,
                suppressHeaderMenuButton: true,
                suppressHeaderFilterButton: true,
                suppressHeaderContextMenu: true,
                valueGetter: (params) => {
                    if (day > Number(params.data?.days_in_month || 31)) return null
                    return params.data?.days?.[String(day)] || null
                },
                valueFormatter: (params) => formatHistoryDay(params.value),
                cellStyle: (params) => getHistoryDayStyle(
                    params.value,
                    day <= Number(params.data?.days_in_month || 31),
                ),
                tooltipValueGetter: (params) => getHistoryDayTooltip(params.value),
            }
        })

        return [
            {field: 'month_label', headerName: 'Месяц', width: 78, pinned: 'left'},
            {
                field: 'org_name',
                headerName: 'Подрядчик',
                width: 170,
                minWidth: 130,
                pinned: 'left',
            },
            {field: 'plan', headerName: 'План', width: 58, pinned: 'left'},
            {field: 'fact', headerName: 'Факт', width: 58, pinned: 'left'},
            {field: 'level', headerName: 'Степень', width: 90, pinned: 'left'},
            ...dayColumns,
        ]
    }, [])

    return (
        <div className="graph-history-overlay" onMouseDown={(event) => {
            if (event.target === event.currentTarget) onClose()
        }}>
            <div className="graph-history-modal">
                <div className="graph-history-modal-header">
                    <div>
                        <h2>Скважина {row?.wells?.name}</h2>
                        <span>
                            Куст {row?.bush || '—'} · {row?.deposit || '—'}
                        </span>
                    </div>
                    <div className="graph-history-modal-controls">
                        <label>
                            <span>Период</span>
                            <select value={months} onChange={(event) => setMonths(event.target.value)}>
                                <option value="5">5 месяцев</option>
                                <option value="12">12 месяцев</option>
                                <option value="24">24 месяца</option>
                                <option value="all">Вся история</option>
                            </select>
                        </label>
                        <button type="button" onClick={onClose} aria-label="Закрыть">×</button>
                    </div>
                </div>

                {error && <div className="graph-history-error">{error}</div>}

                <div className="ag-theme-alpine graph-history-table">
                    <AgGridReact
                        rowData={rows}
                        columnDefs={columnDefs}
                        rowHeight={26}
                        headerHeight={28}
                        defaultColDef={{
                            sortable: false,
                            filter: false,
                            floatingFilter: false,
                            resizable: false,
                            suppressMovable: true,
                            suppressHeaderMenuButton: true,
                            suppressHeaderFilterButton: true,
                            suppressHeaderContextMenu: true,
                        }}
                        overlayNoRowsTemplate={loading ? 'Загружаем график...' : 'История графика отсутствует'}
                    />
                </div>
            </div>
        </div>
    )
}

function formatHistoryDay(value) {
    if (!value || typeof value !== 'object') return ''
    const state = getGraphDayState(value)
    if (state.kind === 'reason') return getReasonAbbreviation(state.reason)
    if (state.kind === 'planned') return 'п'
    return state.maxDepth ? String(Math.round(Number(state.maxDepth))) : ''
}

function getHistoryDayStyle(value, isCalendarDay) {
    const base = {
        paddingLeft: 0,
        paddingRight: 0,
        textAlign: 'center',
        border: '1px solid #dde3ec',
        fontWeight: 700,
    }
    if (!isCalendarDay) return {...base, background: '#d0d5dd', color: '#667085'}
    if (!value || typeof value !== 'object') return {...base, background: '#ffffff'}

    const state = getGraphDayState(value)
    const styles = {
        done_planned: {background: '#81c784', color: '#1b5e20'},
        done: {background: '#81c784', color: '#1b5e20'},
        done_outside_plan: {
            background: 'linear-gradient(135deg, #81c784 0 49%, #fff 50% 100%)',
            color: '#1b5e20',
        },
        manual_planned: {background: '#2e7d32', color: '#ffffff'},
        manual_outside_plan: {
            background: 'linear-gradient(135deg, #2e7d32 0 49%, #fff 50% 100%)',
            color: '#1b5e20',
        },
        no_pass: {background: '#fff176', color: '#795548'},
        ppk_pending: {
            background: 'linear-gradient(135deg, #fff176 0 49%, #66bb6a 50% 100%)',
            color: '#263238',
        },
        reason: {
            background: getReasonStripeBackground(state.reason),
            color: '#475467',
        },
        planned: {background: '#b3e5fc', color: '#01579b', fontWeight: 500},
    }
    return {...base, ...(styles[state.kind] || {background: '#ffffff', color: '#344054'})}
}

function getHistoryDayTooltip(value) {
    if (!value || typeof value !== 'object') return ''
    const state = getGraphDayState(value)
    const lines = []
    if (state.maxDepth) lines.push(`Глубина: ${Number(state.maxDepth).toFixed(1)} м`)
    if (state.ppkLimit?.depth) lines.push(`Подтвержденный ППК: ${state.ppkLimit.depth} м`)
    if (state.reason) lines.push(`Причина: ${state.reason}`)
    if (state.bsiItems?.length) lines.push(`БСИ: ${state.bsiItems.length}`)
    return lines.join('\n')
}

export default GraphWellHistoryModal
